import { useMemo, useState } from 'react';
import { segmentPhrase } from '../lib/greek';
import { respell, respellKey } from '../lib/respell';

interface Props {
  text: string;
  slot?: string | null;
  size?: 'hero' | 'large' | 'body';
  /** IPA for the sounds-like spelling. Without it the phrase is not tappable. */
  ipa?: string;
  /** Show the sounds-like spelling without a tap. */
  showRespelling?: boolean;
  /** Mark stressed syllables (on by default). */
  stress?: boolean;
  className?: string;
}

/**
 * A Greek phrase. The stressed syllable of each word is in colour, and the swappable slot (where
 * asked for) is underlined. Tapping it shows how it sounds in English letters. Screen readers
 * get the plain phrase, marked as Greek so it is read with a Greek voice.
 */
export function GreekText({ text, slot, size = 'body', ipa, showRespelling = false, stress = true, className = '' }: Props) {
  const [open, setOpen] = useState(false);
  const segments = useMemo(() => (stress ? segmentPhrase(text, slot) : [{ text, stressed: false, slot: false }]), [text, slot, stress]);
  const sounds = useMemo(() => (ipa ? respell(ipa) : null), [ipa]);
  const shown = showRespelling || open;

  // Consecutive segments in the slot share one underline, so it reads as one swappable part.
  const runs: Array<{ slot: boolean; parts: typeof segments }> = [];
  for (const seg of segments) {
    const last = runs[runs.length - 1];
    if (last && last.slot === seg.slot) last.parts.push(seg);
    else runs.push({ slot: seg.slot, parts: [seg] });
  }
  const visual = (
    <span className={`greek greek-${size} ${className}`} aria-hidden="true">
      {runs.map((run, r) => {
        const parts = run.parts.map((p, i) =>
          p.stressed && stress ? (
            <span key={i} className="stress">
              {p.text}
            </span>
          ) : (
            <span key={i}>{p.text}</span>
          ),
        );
        return run.slot ? (
          <span key={r} className="slot">
            {parts}
          </span>
        ) : (
          <span key={r}>{parts}</span>
        );
      })}
    </span>
  );
  const spoken = (
    <span className="sr-only" lang="el">
      {text}
    </span>
  );

  if (!sounds) {
    return (
      <div>
        {spoken}
        {visual}
      </div>
    );
  }

  return (
    <div>
      {showRespelling ? (
        <div>
          {spoken}
          {visual}
        </div>
      ) : (
        <button type="button" className="greek-button" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          {spoken}
          {visual}
          <span className="sr-only">{open ? 'Hide pronunciation' : 'Show pronunciation'}</span>
        </button>
      )}
      {shown ? (
        <div className="reveal">
          <p className="respell">
            <span className="sr-only">Sounds like: </span>
            {sounds}
          </p>
          {respellKey(sounds).length > 0 && <p className="respell-hint">{respellKey(sounds).join(' · ')}</p>}
        </div>
      ) : (
        <p className="respell-hint" aria-hidden="true">
          Tap the Greek for a pronunciation guide
        </p>
      )}
    </div>
  );
}
